<template lang="pug">
	.audio-tracker-vumeter(ref="vumeter", :class="orientation", :style="{ '--size': this.size || '100%'}")
		template(v-if="$scopedSlots.vumeter", :dbLevel="mono ? dbLevel : dbLevelL", :peakDbLevel="mono ? peakDbLevel : peakDbLevelL", :peakDbLevelSmoothing="mono ? peakDbLevelSmoothing : peakDbLevelLSmoothing", :avgPowerDecibels="mono ? avgPowerDecibels : avgPowerDecibelsL", :peakInstantaneousPowerDecibels="mono ? peakInstantaneousPowerDecibels : peakInstantaneousPowerDecibelsL")
		template(v-else)
			.full-width.full-height(:class="[getFlexOrientation()]")
				.align-center(:class="[getFlexOrientation(true)]")
					.channel(v-if="!mono && !compact") L
					.vu-meter(ref="VUMeterScale", :class="[orientation, compact ? 'compact' : '']", :style="{ '--width': thickness || (compact ? '4px' : '14px') }")
						.db-level(:style="{ '--value': mono ? currentDbLevel : currentDbLevelL, '--vumeter-width': `${width}px`, '--valuePercentage': `${Math.min(100, 100 * ((mono ? currentDbLevel : currentDbLevelL) + delta) / totalScaleDb || 0)}%`, '--redPointPercentage': `${redPointPercentage}%`, '--yellowPointPercentage': `${yellowPointPercentage}%` }")
						.borders(:style="{ '--tile-width': `${Math.max(2, Math.floor(width / (totalScaleDb / step)))}px` }")
						.db-peak(:style="{ '--value': mono ? currentDbLevelPeak : currentDbLevelPeakL, '--valuePercentage': `${Math.min(100, 100 * ((mono ? currentDbLevelPeak : currentDbLevelPeakL) + delta) / totalScaleDb || 0)}%` }")
				.spacer(v-if="showRuler", :class="[orientation]")
				.align-center(v-if="!compact && showRuler", :class="[getFlexOrientation(true)]")
					.channel
					.vu-meter-ruler-holder.flex-1(:class="[orientation]")
						.vu-meter-ruler
							AudioTrackerRuler(ref="ruler", :width="getRulerWidth", fontSize="8px", :tickVisible="tickVisible", :totalTime="totalScaleDb + 1", :orientation="orientation", :reverse="orientation === 'vertical'", :pixelsPerSecond="() => getRulerWidth() / totalScaleDb", :isTime="false", :valueHandler="rulerValueHandler", :specifiedTickInterval="1", :isFixedWidth="true")
				.align-center(v-if="!mono", :class="[getFlexOrientation(true)]")
					.channel(v-if="!compact") R
					.vu-meter(:class="[orientation, compact ? 'compact' : '']", :style="{ '--width': thickness || (compact ? '4px' : '14px') }")
						.db-level(:style="{ '--value': mono ? currentDbLevel : currentDbLevelR, '--vumeter-width': `${width}px`, '--valuePercentage': `${Math.min(100, 100 * ((mono ? currentDbLevel : currentDbLevelR) + delta) / totalScaleDb || 0)}%`, '--redPointPercentage': `${redPointPercentage}%`, '--yellowPointPercentage': `${yellowPointPercentage}%` }")
						.borders(:style="{ '--tile-width': `${Math.max(2, Math.floor(width / (totalScaleDb / step)))}px` }")
						.db-peak(:style="{ '--value': mono ? currentDbLevelPeak : currentDbLevelPeakR, '--valuePercentage': `${Math.min(100, 100 * ((mono ? currentDbLevelPeak : currentDbLevelPeakR) + delta) / totalScaleDb || 0)}%` }")
</template>

<script>
	import _ from 'lodash';
	import $ from 'jquery';
	import VueTypes from 'vue-types';
	import AudioTrackerRuler from './AudioTrackerRuler.vue';

	export default {
		translationCategory: 'audioTracker.AudioTrackerVUMeter',
		name: 'audio-tracker-vumeter',
		components: { AudioTrackerRuler },
		props: {
			track: VueTypes.any.def(null),
			tracker: VueTypes.any.def(null),
			filters: VueTypes.any.def(null),
			isPlaying: VueTypes.bool.def(false),
			min: VueTypes.number.def(-60),
			max: VueTypes.number.def(3),
			peakSmoothingLevel: VueTypes.number.def(7),
			orientation: VueTypes.oneOf(['vertical', 'horizontal']).def('horizontal'),
			mono: VueTypes.bool.def(false),
			compact: VueTypes.bool.def(false),
			step: VueTypes.number.def(1),
			redPoint: VueTypes.number.def(-6),
			yellowPoint: VueTypes.number.def(-20),
			size: VueTypes.any.def(null),
			thickness: VueTypes.any.def(null),
			tickVisible: VueTypes.bool.def(true),
			showRuler: VueTypes.bool.def(true),
			showRulerMaxValues: VueTypes.bool.def(true),
			manualValue: VueTypes.any.def(null),
			valueResolver: VueTypes.any.def(null)
		},
		data() {
			return {
				dbLevelL: null,
				dbLevelR: null,
				peakDbLevelL: null,
				peakDbLevelR: null,
				peakPowerL: null,
				peakPowerR: null,
				peakDbLevelLSmoothing: this.min,
				peakDbLevelRSmoothing: this.min,
				timerInterval: null,
				avgPowerDecibelsL: null,
				avgPowerDecibelsR: null,
				peakInstantaneousPowerDecibelsL: null,
				peakInstantaneousPowerDecibelsR: null,
				width: 200,
				interval: null,
				sizesCalculated: false
			};
		},
		computed: {
			delta() {
				return Math.abs(Math.min(0, this.min));
			},
			totalScaleDb() {
				return this.max - this.min;
			},
			redPointPercentage() {
				if (this.max <= 0) return 100;
				return (this.redPoint - this.min) * 100 / this.totalScaleDb || 100;
			},
			yellowPointPercentage() {
				if (this.max <= 0) return 100;
				return (this.yellowPoint - this.min) * 100 / this.totalScaleDb || 100;
			},
			dbLevel() {
				if (this.dbLevelR === null && this.dbLevelL === null) return null;
				return Math.max(this.dbLevelR || 0, this.dbLevelL || 0);
			},
			peakDbLevel() {
				if (this.peakDbLevelR === null && this.peakDbLevelL === null) return null;

				if (this.peakPowerL || this.peakPowerR) {
					return this.peakPowerL || this.peakPowerR ? (10 * Math.log10((this.peakPowerL || 0) + (this.peakPowerR || 0))) : this.min;
				} else {
					return Math.max(this.peakDbLevelR || 0, this.peakDbLevelL || 0);
				}
			},
			peakDbLevelSmoothing() {
				if (this.peakDbLevelRSmoothing === null && this.peakDbLevelLSmoothing === null) return null;
				return Math.max(this.peakDbLevelRSmoothing || 0, this.peakDbLevelLSmoothing || 0);
			},
			peakInstantaneousPowerDecibels() {
				if (this.peakInstantaneousPowerDecibelsR === null && this.peakInstantaneousPowerDecibelsL === null) return null;
				return Math.max(this.peakInstantaneousPowerDecibelsR || 0, this.peakInstantaneousPowerDecibelsL || 0);
			},
			avgPowerDecibels() {
				if (this.avgPowerDecibelsR === null && this.avgPowerDecibelsL === null) return null;
				return Math.max(this.avgPowerDecibelsR || 0, this.avgPowerDecibelsL || 0);
			},
			currentDbLevel() {
				return (this.isPlaying || this.manualValue) && this.dbLevel !== null ? this.dbLevel : this.min;
			},
			currentDbLevelL() {
				return (this.isPlaying || this.manualValue) && this.dbLevelL !== null ? this.dbLevelL : this.min;
			},
			currentDbLevelR() {
				return (this.isPlaying || this.manualValue) && this.dbLevelR !== null ? this.dbLevelR : this.min;
			},
			currentDbLevelPeak() {
				return (this.isPlaying || this.manualValue) && this.peakDbLevelSmoothing !== null ? this.peakDbLevelSmoothing : this.min;
			},
			currentDbLevelPeakL() {
				return (this.isPlaying || this.manualValue) && this.peakDbLevelLSmoothing !== null ? this.peakDbLevelLSmoothing : this.min;
			},
			currentDbLevelPeakR() {
				return (this.isPlaying || this.manualValue) && this.peakDbLevelRSmoothing !== null ? this.peakDbLevelRSmoothing : this.min;
			}
		},
		watch: {
			isPlaying(v) {
				this.setMetering(v);
			},
			manualValue(v) {
				this.processManualValue(v);
			}
		},
		mounted() {
			if (__DEBUG__)
				window.vumeter = this;
			if (this.isPlaying)
				this.setMetering(true);
		},
		beforeDestroy() {
			this.setMetering(false);
		},
		methods: {
			processManualValue(v) {
				if (!v) {
					this.sizesCalculated = false;
					return;
				}
				if (!this.sizesCalculated)
					this.calcSizes();
				if (v.percent || v.percentL || v.percentR) {
					var value = this.totalScaleDb * Math.max(0, Math.min(100, v.percent)) / 100 + this.min;
					var valueL = this.totalScaleDb * Math.max(0, Math.min(100, v.percent)) / 100 + this.min;
					var valueR = this.totalScaleDb * Math.max(0, Math.min(100, v.percent)) / 100 + this.min;
					this.dbLevelL = valueL || value;
					this.dbLevelR = valueR || value;
				} else if (v.value || v.valueL || v.valueR) {
					this.dbLevelL = v.valueL || v.value;
					this.dbLevelR = v.valueR || v.value;
				}
			},
			getRulerWidth() {
				return this.orientation === 'horizontal' ? $(this.$refs.VUMeterScale).width() : $(this.$refs.VUMeterScale).height();
			},
			rulerValueHandler(v) {
				if ((v.num + this.min) > this.max) return false;
				return {
					showNumber: this.orientation === 'horizontal'
						? (this.showRulerMaxValues && (v.num + this.min) === this.max) || v.num % 10 === 0
						: v.num % 10 === 0,
					number: v.num + this.min
				}
			},
			getRoundedValue(v) {
				return Math.floor(v / this.step) * this.step;
			},
			getFlexOrientation(opposite) {
				return this.orientation === (opposite ? 'horizontal' : 'vertical') ? 'flex-row' : 'flex-col';
			},
			calcSizes() {
				this.sizesCalculated = true;
				this.width = this.getRulerWidth();
			},
			setMetering(v) {
				if (__DEBUG__)
					console.debug(`Set metering ${v}`);
				
				if (this.$meteringTimeout)
					clearTimeout(this.$meteringTimeout);
				if (this.interval)
					clearInterval(this.interval);

				this.peakDbLevelL = this.min;
				this.peakDbLevelR = this.min;
				this.peakPowerL = null;
				this.peakPowerR = null;
				this.dbLevelL = this.min;
				this.dbLevelR = this.min;
				this.lastTime = null;

				if (v) {
					this.calcSizes();
					const initTimeout = () => setTimeout(() => {
						if (!this._isDestroyed && this.isPlaying) {
							this.doMetering();
							initTimeout();
						}
					}, 1000 / 90);

					this.$meteringTimeout = initTimeout();
					var delta = 0.01;
					this.interval = setInterval(() => {
						if (this.dbLevelL !== null && this.dbLevelL > (this.peakDbLevelLSmoothing || 0) || this.peakDbLevelL === null)
							this.peakDbLevelLSmoothing = this.dbLevelL;
						else {
							this.peakDbLevelLSmoothing = Math.max(this.min, Math.min(this.max, this.peakDbLevelLSmoothing - delta * this.peakSmoothingLevel));
						}
						if (this.dbLevelR !== null && this.dbLevelR > (this.peakDbLevelRSmoothing || 0) || this.peakDbLevelR === null)
							this.peakDbLevelRSmoothing = this.dbLevelR;
						else {
							this.peakDbLevelRSmoothing = Math.max(this.min, Math.min(this.max, this.peakDbLevelRSmoothing - delta * this.peakSmoothingLevel));
						}
					}, 40);
				}
			},
			doMetering() {
				if (this.filters?.stereoAnalyser) {
					const sampleBufferL = new Float32Array(this.filters.stereoAnalyser.fftSize);
					const sampleBufferR = new Float32Array(this.filters.stereoAnalyser.fftSize);
					this.filters.stereoAnalyser.getFloatTimeDomainData(sampleBufferL, sampleBufferR);

					var processBuffer = (sampleBuffer) => {
						// Compute average power over the interval.
						let sumOfSquares = 0;
						for (let i = 0; i < sampleBuffer.length; i++) {
							sumOfSquares += sampleBuffer[i] ** 2;
						}
						const avgPowerDecibels = 10 * Math.log10(sumOfSquares / sampleBuffer.length);

						// Compute peak instantaneous power over the interval.
						let peakInstantaneousPower = 0;
						for (let i = 0; i < sampleBuffer.length; i++) {
							const power = sampleBuffer[i] ** 2;
							peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
						}
						const peakInstantaneousPowerDecibels = 10 * Math.log10(peakInstantaneousPower);

						return {
							avgPowerDecibels,
							peakInstantaneousPowerDecibels,
							peakInstantaneousPower,
							dbLevel: Math.max(this.min, Math.min(this.max, peakInstantaneousPowerDecibels))
						}
					};

					var dataL = processBuffer(sampleBufferL);
					this.avgPowerDecibelsL = this.getRoundedValue(dataL.avgPowerDecibels);
					this.peakInstantaneousPowerDecibelsL = this.getRoundedValue(dataL.peakInstantaneousPowerDecibels);

					let processLevel = (l, key) => {
						if (!this[`$${key}_metering_values`]) {
							this[`$${key}_metering_values`] = [];
						}
						let arr = this[`$${key}_metering_values`];
						arr.push(l);

						if (arr.length > 10) {
							arr.splice(0, 1);
						}

						/*let vals = _.orderBy(arr, f => f);
						return vals[Math.ceil(vals.length / 2)];*/

						return _.maxBy(arr, f => f);
					}

					let levelL = processLevel(this.getRoundedValue(dataL.dbLevel), 'left');
					this.dbLevelL = levelL;
					this.peakDbLevelL = Math.max(this.peakDbLevelL, this.dbLevelL);
					this.peakPowerL = Math.max(this.peakPowerL, dataL.peakInstantaneousPower);

					var dataR = processBuffer(sampleBufferR);
					this.avgPowerDecibelsR = this.getRoundedValue(dataR.avgPowerDecibels);
					this.peakInstantaneousPowerDecibelsR = this.getRoundedValue(dataR.peakInstantaneousPowerDecibels);

					let levelR = processLevel(this.getRoundedValue(dataR.dbLevel), 'right');
					this.dbLevelR = levelR;
					this.peakDbLevelR = Math.max(this.peakDbLevelR, this.dbLevelR);
					this.peakPowerR = Math.max(this.peakPowerR, dataR.peakInstantaneousPower);
				} else {
					this.dbLevelL = this.dbLevelR = this.valueResolver ? this.valueResolver() : this.manualValue;
				}

				this.$emit('change', {
					dbLevelL: this.dbLevelL,
					peakDbLevelL: this.peakDbLevelL,
					dbLevelR: this.dbLevelR,
					peakDbLevelR: this.peakDbLevelR,
				});
			}
		}
	};
</script>
<style lang='scss'>
	.audio-tracker-vumeter {
		@import "main/themes/generated/variables.base.scss";

		&.horizontal {
			width: var(--size);
		}

		&.vertical {
			height: var(--size);
		}

		.channel {
			width: 15px;
			height: 15px;
			font-size: 10px;
			text-align: center;
			opacity: 0.4;
		}

		.spacer.vertical {
			width: 1px;
		}

		.spacer.horizontal {
			height: 1px;
		}

		.vu-meter-ruler-holder {

			&.horizontal {
				height: 12px;
				width: 100%;
			}

			&.vertical {
				width: 16px;
				height: 100%;

				.tick {
					text-align: center;
				}
			}

			position: relative;

			> .vu-meter-ruler {
				position: absolute;
				overflow: hidden;
				width: 100%;
				height: 100%;
				top: 0px;
				left: 0px;
			}
		}

		.vu-meter {
			position: relative;
			background: #d5d5d5;

			&.vertical {
				height: 100%;
				width: var(--width);

				.db-level {
					position: absolute;
					width: 100%;
					bottom: 0px;
					background: linear-gradient(0deg, #00cc00 0%, #00cc00 var(--yellowPointPercentage), #fdff00 var(--yellowPointPercentage), #fdff00 var(--redPointPercentage), rgba(255,50,50,1) var(--redPointPercentage), rgba(255,50,50,1) 100%);
					background-size: var(--vumeter-width) var(--vumeter-width);
					background-position: bottom;
					height: calc(var(--valuePercentage) + 0%);
				}

				.db-peak {
					position: absolute;
					width: 100%;
					height: 1px;
					bottom: calc(var(--valuePercentage) + 0%);
					background: #333;
				}

				.borders {
					position: absolute;
					width: 100%;
					height: 100%;

					background-image: linear-gradient(0deg, transparent 0px, transparent calc(var(--tile-width) - 1px), #d5d5d5 calc(var(--tile-width) - 1px));
					background-size: var(--tile-width) var(--tile-width);
					background-position: 0 0, 0px 0px;
				}
			}

			&.horizontal {
				width: 100%;
				height: var(--width);

				&.compact {
					height: var(--width);
				}

				.db-level {
					position: absolute;
					height: 100%;
					left: 0px;
					background: linear-gradient(90deg, #00cc00 0%, #00cc00 var(--yellowPointPercentage), #fdff00 var(--yellowPointPercentage), #fdff00 var(--redPointPercentage), rgba(255,50,50,1) var(--redPointPercentage), rgba(255,50,50,1) 100%);
					background-size: var(--vumeter-width);
					width: calc(var(--valuePercentage) + 0%);
					transition: 0.1s;
				}

				.db-peak {
					position: absolute;
					height: 100%;
					width: 1px;
					left: calc(var(--valuePercentage) + 0%);
					background: #333;
				}

				.borders {
					position: absolute;
					width: 100%;
					height: 100%;

					background-image: linear-gradient(90deg, transparent 0px, transparent calc(var(--tile-width) - 1px), #d5d5d5 calc(var(--tile-width) - 1px));
					background-size: var(--tile-width) var(--tile-width);
					background-position: 0 0, 0px 0px;
				}
			}
		}
	}
</style>
